import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  beginLocationsContext,
  endLocationsContext,
} from 'src/state/slices/locations';
import { METROBI_TIMING } from './metrobi';
import { isDefined } from 'src/utils';
import {
  Autocomplete,
  Button,
  NativeSelect,
  Spacer,
  TextInput,
} from '@odekoteam/doppio';
import SearchSvg from 'assets/svgs/search.svg';
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { TableVirtuoso } from 'react-virtuoso';
import { BulkEditTab } from 'src/components/Modal/BulkEditLocationsModal';
import { TableHeaderCells } from 'src/components/Table';
import { GET_COHORTS } from 'src/graphql/requests/getCohorts';
import { GET_LOCATIONS } from 'src/graphql/requests/getLocations';
import { GetLocation } from 'src/graphql/types/delivery';
import {
  GGetCohortsQuery,
  GGetLocationsQuery,
  GScheduledDeliveryTimeSourceEnum,
} from 'src/graphql/types/generated';
import { useAppDispatch, useAppSelector } from 'src/hooks/store';
import {
  selectedWarehouseSelector,
  warehousesSelector,
} from 'src/state/selectors/delivery';
import { setSelectedWarehouseId } from 'src/state/slices/delivery';
import { ModalType, showModal } from 'src/state/slices/modal';
import {
  readManageLocationsHandoff,
  removeManageLocationsHandoff,
} from 'src/utils/locations';
import { Flex } from 'theme-ui';
import { useQuery } from 'urql';
import { LoaderSpinner } from '../Loader';
import { tableStyle } from '../Table/style';
import DownloadLocationsCsv from './DownloadLocationsCsv';
import Loader from './Loader';
import LocationRow from './LocationRow';
import {
  Item,
  MetrobiFilter,
  metrobiFilterItems,
  RoutingFilter,
  ScheduledDeliveryTimeSourceFilter,
  allCitiesItem,
  allCohortsItem,
  allStatesItem,
  alphabeticalByName,
  getFilteredLocations,
  noCohortsItem,
  routingFilterItems,
  uniqueItems,
} from './utils';

const labels = [
  '<select>',
  'Location Id',
  'Location',
  'City',
  'State',
  'Zip Code',
  'Cohort',
  'Service Time',
  'Automatic Routing',
  'Enforce Item Scanning',
  'Deliver Within Store Hours',
  'Customer Pickup',
  'Print Delivery Sheet',
  'Include Financials on Delivery Sheet',
  'Store Hours Confirmed',
  'Scheduled Delivery Time',
];

type GetCohortsVariables = {
  routePlanInput: { warehouseId?: string; nightEnd?: string };
};

type GetLocationsVariables = {
  input: { warehouseId?: string };
  first: number;
  after?: string;
};

const table = ({
  style,
  ...props
}: React.TableHTMLAttributes<HTMLTableElement>): JSX.Element => (
  <table {...props} style={{ ...style, width: '96vw' }} />
);

// This existing screen coordinates pagination, handoffs, filters and warehouse state.
// eslint-disable-next-line complexity
const WarehouseLocationsTable = (): JSX.Element => {
  const { deliveryServiceMetrobiEligibility = false } = useFlags();
  const contextId = useId();
  const metrobi = useAppSelector((state) => state.locations.metrobi);
  const routingLocations = useAppSelector(
    (state) => state.locations.routingLocations,
  );
  const [metrobiFilter, setMetrobiFilter] = useState(MetrobiFilter.All);
  const tableLabels = labels.flatMap((label) =>
    deliveryServiceMetrobiEligibility && label === 'Store Hours Confirmed'
      ? ['Metrobi Eligible', label]
      : [label],
  );
  const dispatch = useAppDispatch();

  const selectedWarehouse = useAppSelector(selectedWarehouseSelector);
  const warehouses = useAppSelector(warehousesSelector);
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.toString();
  const handoff = useMemo(
    () => readManageLocationsHandoff(new URLSearchParams(search)),
    [search],
  );
  const handledHandoffRef = useRef<string>();
  const warehouseSwitchHandoffRef = useRef<string>();
  const [allLocations, setAllLocations] = useState<GetLocation[]>([]);
  const [endCursor, setEndCursor] = useState<string | undefined>(undefined);

  const [selectedLocations, setSelectedLocations] = useState<GetLocation[]>([]);
  const [isAllSelected, setIsAllSelected] = useState<boolean>(false);
  const [handoffMessage, setHandoffMessage] = useState<string>();
  // filters
  const [searchValue, setSearchValue] = useState('');
  const [routingFilter, setRoutingFilter] = useState(
    RoutingFilter.allLocations,
  );
  const [
    scheduledDeliveryTimeSourceFilter,
    setScheduledDeliveryTimeSourceFilter,
  ] = useState<string>(ScheduledDeliveryTimeSourceFilter.ALL_SOURCES);
  const [stateFilter, setStateFilter] = useState(allStatesItem.value);
  const [cityFilter, setCityFilter] = useState(allCitiesItem.value);
  const [zipFilter, setZipFilter] = useState('');
  const [cohortFilter, setCohortFilter] = useState(allCohortsItem.value);
  // dropdown inputs
  const [stateItems, setStateItems] = useState<Item[]>([allStatesItem]);
  const [cityItems, setCityItems] = useState<Item[]>([allCitiesItem]);
  const [zipItems, setZipItems] = useState<Item[]>([]);
  const [cohortItems, setCohortItems] = useState<Item[]>([
    allCohortsItem,
    noCohortsItem,
  ]);
  const handoffWarehouseMatches =
    !handoff.isPresent ||
    (handoff.isValid && selectedWarehouse?.id === handoff.warehouseId);

  const [
    {
      data: cohortData,
      fetching: cohortFetching,
      error: cohortError,
      operation: cohortOperation,
    },
  ] = useQuery<GGetCohortsQuery, GetCohortsVariables>({
    query: GET_COHORTS,
    variables: {
      routePlanInput: {
        warehouseId: selectedWarehouse?.id,
        nightEnd: selectedWarehouse?.currentNightEnd,
      },
    },
    pause: !selectedWarehouse?.id || !handoffWarehouseMatches,
  });

  const [{ data, fetching, error, operation: locationsOperation }] = useQuery<
    GGetLocationsQuery,
    GetLocationsVariables
  >({
    query: GET_LOCATIONS,
    requestPolicy: 'network-only',
    variables: {
      input: {
        warehouseId: selectedWarehouse?.id,
      },
      first: 100,
      after: endCursor,
    },
    pause: !selectedWarehouse?.id || !handoffWarehouseMatches,
  });
  const locationsDataMatchesWarehouse =
    !locationsOperation?.variables ||
    locationsOperation.variables.input.warehouseId === selectedWarehouse?.id;
  const cohortDataMatchesWarehouse =
    !cohortOperation?.variables ||
    cohortOperation.variables.routePlanInput.warehouseId ===
      selectedWarehouse?.id;

  useLayoutEffect(() => {
    dispatch(beginLocationsContext(contextId));
    return (): void => {
      dispatch(endLocationsContext(contextId));
    };
  }, [contextId, dispatch]);

  const confirmedLocations = useMemo(() => {
    const settingsById = new Map(
      routingLocations.map((settings) => [settings.locationId, settings]),
    );
    return allLocations.map((location) => {
      const settings = settingsById.get(location.id);
      return {
        ...location,
        ...settings,
        scheduledDeliveryTimeSource: (settings?.scheduledDeliveryTimeSource ??
          location.scheduledDeliveryTimeSource) as GScheduledDeliveryTimeSourceEnum,
        ...(metrobi?.contextId === contextId
          ? metrobi.locations[location.id]
          : undefined),
      };
    });
  }, [allLocations, routingLocations, metrobi, contextId]);
  const filteredLocations = useMemo(
    () =>
      getFilteredLocations(confirmedLocations, {
        searchValue,
        routingFilter,
        scheduledDeliveryTimeSourceFilter,
        stateFilter,
        cityFilter,
        zipFilter,
        cohortFilter,
        cohorts: cohortData?.routingImportPlan?.cohorts,
        metrobiFilter: deliveryServiceMetrobiEligibility
          ? metrobiFilter
          : MetrobiFilter.All,
      }).sort(alphabeticalByName),
    [
      confirmedLocations,
      searchValue,
      routingFilter,
      scheduledDeliveryTimeSourceFilter,
      stateFilter,
      cityFilter,
      zipFilter,
      cohortFilter,
      cohortData,
      deliveryServiceMetrobiEligibility,
      metrobiFilter,
    ],
  );
  const visibleSelectedLocations = filteredLocations.filter((location) =>
    selectedLocations.some((selected) => selected.id === location.id),
  );
  useEffect(() => {
    const visibleIds = new Set(
      filteredLocations.map((location) => location.id),
    );
    setSelectedLocations((current) =>
      current.filter((location) => visibleIds.has(location.id)),
    );
    setIsAllSelected(false);
  }, [filteredLocations]);

  const onHeaderSelected = (value: boolean): void => {
    let newSelectedLocations: GetLocation[] = [...selectedLocations];
    filteredLocations.forEach((filteredLocation) => {
      const locationIndex = selectedLocations.findIndex(
        (selectedLocation) => selectedLocation.id === filteredLocation.id,
      );
      const isLocationSelected = locationIndex !== -1;
      if (
        value &&
        !isLocationSelected &&
        isDefined(filteredLocation.includeInAutomatedRouting)
      ) {
        // if checked, add to selected locations
        newSelectedLocations = [...newSelectedLocations, filteredLocation];
      } else if (!value && isLocationSelected) {
        // if unchecked, add to selected locations
        newSelectedLocations = newSelectedLocations.filter(
          (newSelectedLocation) =>
            newSelectedLocation.id !== filteredLocation.id,
        );
      }
    });
    setSelectedLocations(newSelectedLocations);
  };

  const onUpdateLocationsButtonClicked = (): void => {
    dispatch(
      showModal({
        type: ModalType.BulkEditLocationsModal,
        dismissButtonLabel: 'Cancel',
        variables: {
          locations: visibleSelectedLocations,
          metrobiContextId: contextId,
          cohorts: cohortData?.routingImportPlan?.cohorts ?? [],
        },
      }),
    );
  };

  const itemContent = (
    _index: number,
    location?: GetLocation,
  ): JSX.Element | null => {
    if (location)
      return (
        <LocationRow
          location={location}
          showMetrobi={deliveryServiceMetrobiEligibility}
          contextId={contextId}
          searchValue={searchValue}
          setSelectedLocations={setSelectedLocations}
          selectedLocations={visibleSelectedLocations}
          cohorts={cohortData?.routingImportPlan?.cohorts}
        />
      );
    return null;
  };
  const fixedHeaderContent = (): JSX.Element => (
    <TableHeaderCells
      labels={tableLabels}
      onHeaderSelected={onHeaderSelected}
      isAllSelected={isAllSelected}
      setIsAllSelected={setIsAllSelected}
    />
  );

  // Accumulate every page for the selected warehouse. A map prevents cached
  // pages or repeated responses from duplicating rows.
  useEffect(() => {
    if (!locationsDataMatchesWarehouse) return;
    const nodes = data?.customerLocations.nodes;
    if (!nodes) return;

    setAllLocations((current) => {
      const byId = new Map(
        current.map((location) => [location.id, location] as const),
      );
      nodes.forEach((location) => {
        if (location) byId.set(location.id, location);
      });
      return Array.from(byId.values()).sort(alphabeticalByName);
    });

    const pageInfo = data.customerLocations.pageInfo;
    if (pageInfo.hasNextPage && pageInfo.endCursor) {
      setEndCursor(pageInfo.endCursor);
    }
  }, [data, locationsDataMatchesWarehouse]);

  const consumeHandoff = useCallback(
    (message?: string): void => {
      handledHandoffRef.current = search;
      setHandoffMessage(message);
      setSearchParams(removeManageLocationsHandoff(searchParams), {
        replace: true,
      });
    },
    [search, searchParams, setSearchParams],
  );

  // Align the global warehouse before loading or resolving the handoff. Invalid
  // input is consumed without changing the user's current warehouse.
  useEffect(() => {
    if (!handoff.isPresent) {
      handledHandoffRef.current = undefined;
      warehouseSwitchHandoffRef.current = undefined;
      return;
    }
    if (handledHandoffRef.current === search) return;
    if (!handoff.isValid) {
      consumeHandoff('The selected locations link is invalid.');
      return;
    }
    if (!warehouses) return;

    const requestedWarehouse = warehouses.find(
      (warehouse) => warehouse.id === handoff.warehouseId,
    );
    if (!requestedWarehouse) {
      consumeHandoff('The originating warehouse could not be found.');
      return;
    }
    if (selectedWarehouse?.id !== requestedWarehouse.id) {
      if (warehouseSwitchHandoffRef.current === search) return;
      warehouseSwitchHandoffRef.current = search;
      dispatch(setSelectedWarehouseId({ warehouseId: requestedWarehouse.id }));
    } else {
      warehouseSwitchHandoffRef.current = undefined;
    }
  }, [
    handoff,
    search,
    warehouses,
    selectedWarehouse?.id,
    dispatch,
    consumeHandoff,
  ]);

  const currentPageMerged =
    data?.customerLocations.nodes?.every(
      (location) =>
        location === null ||
        allLocations.some(
          (loadedLocation) => loadedLocation.id === location.id,
        ),
    ) ?? false;
  const allLocationsLoaded = Boolean(
    data &&
      locationsDataMatchesWarehouse &&
      !fetching &&
      !data.customerLocations.pageInfo.hasNextPage &&
      currentPageMerged,
  );
  const cohortsLoaded = Boolean(
    cohortData && cohortDataMatchesWarehouse && !cohortFetching,
  );

  // Resolve only against the complete warehouse data, then consume the URL
  // before opening the existing bulk editor. Table filters are intentionally
  // ignored so every requested location can be selected.
  useEffect(() => {
    if (
      !handoff.isPresent ||
      !handoff.isValid ||
      handledHandoffRef.current === search ||
      selectedWarehouse?.id !== handoff.warehouseId
    ) {
      return;
    }
    if (error || cohortError) {
      consumeHandoff('The selected locations could not be loaded.');
      return;
    }
    if (!allLocationsLoaded || !cohortsLoaded) return;

    const requestedIds = new Set(handoff.locationIds);
    const matchedLocations = allLocations.filter((location) =>
      requestedIds.has(location.id),
    );
    const unresolvedCount = requestedIds.size - matchedLocations.length;
    const unresolvedMessage =
      unresolvedCount > 0
        ? `${unresolvedCount} ${
            unresolvedCount === 1 ? 'location' : 'locations'
          } could not be found.`
        : undefined;

    consumeHandoff(unresolvedMessage);
    setIsAllSelected(false);
    setSelectedLocations(matchedLocations);
    if (matchedLocations.length === 0) return;

    dispatch(
      showModal({
        type: ModalType.BulkEditLocationsModal,
        dismissButtonLabel: 'Cancel',
        variables: {
          locations: matchedLocations,
          metrobiContextId: contextId,
          cohorts: cohortData?.routingImportPlan?.cohorts ?? [],
          initialTab: BulkEditTab.AutomatedRouting,
        },
      }),
    );
  }, [
    handoff,
    search,
    selectedWarehouse?.id,
    error,
    cohortError,
    allLocationsLoaded,
    cohortsLoaded,
    contextId,
    allLocations,
    cohortData,
    dispatch,
    consumeHandoff,
  ]);

  // get the dropdown values
  useEffect(() => {
    // get states
    const myStateItems = allLocations
      .map(
        (location: GetLocation): Item => ({
          value: location.address.state,
          label: location.address.state,
        }),
      )
      .filter(uniqueItems);
    setStateItems([allStatesItem, ...myStateItems]);
    // get cities
    const myCityItems = allLocations
      .map(
        (location: GetLocation): Item => ({
          value: location.address.city,
          label: location.address.city,
        }),
      )
      .filter(uniqueItems);
    setCityItems([allCitiesItem, ...myCityItems]);
    // get zips
    const myZipCodeItems = allLocations
      .map(
        (location: GetLocation): Item => ({
          value: location.address.postalCode,
          label: location.address.postalCode,
        }),
      )
      .filter(uniqueItems);
    setZipItems(myZipCodeItems);
    // get cohorts
    const myCohortItems =
      cohortData?.routingImportPlan?.cohorts
        ?.map(
          (cohort): Item => ({
            value: cohort.id,
            label: cohort.name,
          }),
        )
        .filter(uniqueItems) ?? [];
    setCohortItems([allCohortsItem, ...myCohortItems, noCohortsItem]);
  }, [allLocations, selectedWarehouse?.id, cohortData]);

  return (
    <Flex
      id="locations-table"
      tabIndex={-1}
      sx={{ flexDirection: 'column', width: '100vw', marginTop: '20px' }}
    >
      <Flex
        sx={{
          flexDirection: ['column', 'column', 'column', 'row'],
          justifyContent: 'space-between',
          marginBottom: '14px',
          width: '96vw',
          flexWrap: 'wrap',
          gap: '8px',
        }}
      >
        <Flex>
          <Button
            id="update-locations-button"
            isDisabled={
              visibleSelectedLocations.length === 0 ||
              Boolean(metrobi?.pendingIds.length) ||
              Boolean(metrobi?.customerPickupPending)
            }
            onPress={onUpdateLocationsButtonClicked}
          >
            Edit {visibleSelectedLocations.length} locations
          </Button>
          <Spacer width="$1" />
          <TextInput
            iconLeft={<SearchSvg />}
            id="location-search"
            onChangeText={(value): void => setSearchValue(value)}
            placeholder="Search"
            value={searchValue}
          />
        </Flex>
        <Spacer height="$1" />
        <Flex sx={{ flexDirection: ['column', 'column', 'row'] }}>
          <NativeSelect
            id="routing-input-controlled"
            items={routingFilterItems}
            value={routingFilter}
            onValueChange={(value): void =>
              setRoutingFilter(value as RoutingFilter)
            }
          />
          <Spacer width="$1" />
          <NativeSelect
            id="scheduled-delivery-time-input-controlled"
            items={Object.entries(ScheduledDeliveryTimeSourceFilter).map(
              ([key, value]) => ({
                value: value,
                label:
                  key === ScheduledDeliveryTimeSourceFilter.ALL_SOURCES
                    ? 'All Scheduled Delivery Times'
                    : key
                        .split('_')
                        .map(
                          (word) =>
                            word.charAt(0).toUpperCase() +
                            word.slice(1).toLowerCase(),
                        )
                        .join(' '),
              }),
            )}
            value={scheduledDeliveryTimeSourceFilter}
            onValueChange={(value): void => {
              setScheduledDeliveryTimeSourceFilter(value as string);
            }}
          />
          <Spacer width="$1" />
          <NativeSelect
            id="cohort-input-controlled"
            items={cohortItems}
            value={cohortFilter}
            onValueChange={(value): void => setCohortFilter(value as string)}
          />
          <Spacer width="$1" />
          <NativeSelect
            id="state-input-controlled"
            items={stateItems}
            value={stateFilter}
            onValueChange={(value): void => setStateFilter(value as string)}
          />
          <Spacer width="$1" />
          <NativeSelect
            id="city-input-controlled"
            items={cityItems}
            value={cityFilter}
            onValueChange={(value): void => setCityFilter(value as string)}
          />
          <Spacer width="$1" />
          <Autocomplete
            id="zip-input-controlled"
            options={zipItems}
            value={zipFilter}
            placeholder="All Zip Codes"
            onChangeText={(value): void => setZipFilter(value)}
            onSelect={(option): void => setZipFilter(option.value as string)}
          />
          {deliveryServiceMetrobiEligibility && (
            <>
              <Spacer width="$1" />
              <NativeSelect
                id="metrobi-eligibility-filter"
                aria-label="Metrobi eligibility"
                value={metrobiFilter}
                items={metrobiFilterItems}
                onValueChange={(value): void =>
                  setMetrobiFilter(value as MetrobiFilter)
                }
              />
            </>
          )}
        </Flex>
      </Flex>
      {deliveryServiceMetrobiEligibility && (
        <div sx={{ marginBottom: '12px' }}>
          <div role="status" tabIndex={-1} id="metrobi-update-status">
            {metrobi?.contextId === contextId && metrobi.message && (
              <>
                {metrobi.message} {METROBI_TIMING}
              </>
            )}
          </div>
        </div>
      )}
      {error && (
        <div role="alert">
          Locations could not be completely loaded. Reload the page to retry.
        </div>
      )}
      {handoffMessage && (
        <div
          role="alert"
          data-test-id="manage-locations-handoff-message"
          sx={{
            variant: 'text.paragraphSmDefault',
            color: 'secondaryText',
            marginBottom: '10px',
          }}
        >
          {handoffMessage}
        </div>
      )}
      <Flex
        sx={{
          variant: 'text.paragraphLgDefault',
          marginBottom: '6px',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '96vw',
        }}
      >
        <span>
          {allLocations.length} of {data?.customerLocations.totalCount ?? 0}{' '}
          locations loaded
          {!allLocationsLoaded && (
            <LoaderSpinner
              imgStyle={{
                top: 'auto',
                left: 'auto',
                height: '20px',
                margin: '2px 10px',
              }}
            />
          )}
        </span>
        <DownloadLocationsCsv
          locations={filteredLocations}
          showMetrobi={deliveryServiceMetrobiEligibility}
          loading={
            !allLocationsLoaded ||
            Boolean(error) ||
            !cohortsLoaded ||
            Boolean(cohortError)
          }
        />
      </Flex>
      {fetching && allLocations.length === 0 ? (
        <Loader labels={tableLabels} />
      ) : (
        <TableVirtuoso
          sx={{ ...tableStyle }}
          data={filteredLocations}
          computeItemKey={(_index, location): string => location.id}
          useWindowScroll
          fixedHeaderContent={fixedHeaderContent}
          itemContent={itemContent}
          components={{
            Table: table,
          }}
        />
      )}
    </Flex>
  );
};

const LocationsTable = (): JSX.Element => {
  const warehouse = useAppSelector(selectedWarehouseSelector);
  // Remount all filters, selections and request state with the warehouse.
  return <WarehouseLocationsTable key={warehouse?.id ?? 'no-warehouse'} />;
};

export default LocationsTable;
